/**
 * "How this number is derived" — auditable funnel for the Expired KPI.
 *
 * Renders the all -> retained funnel the backend computed (orphan / archived
 * / deleted / org-untagged / no-cert / no-due / not-expired dropped at each
 * step) so the tile's number isn't a black box, plus the off-filter hint for
 * accounts excluded by the covered-only / org-tag filters.
 */
import { useState } from 'react';
import { ChevronDown, Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { CalibrationFunnelStage, CalibrationPlanningResponse } from '../types';

const STAGE_LABELS: Record<string, string> = {
  all: 'All calibration certificates',
  orphan: 'Orphaned (no sensor link)',
  archived: 'Archived',
  deleted: 'Deleted',
  orgUntagged: 'Org untagged / not customer-linked',
  calibrationExcluded: 'Excluded per sensor (Calibration unchecked)',
  noCert: 'No certificate on file',
  noDue: 'No due date',
  notExpired: 'Not yet expired',
  retained: 'Expired (in contract) — the tile number',
};

function humanizeStage(stage: string): string {
  const spaced = stage.replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function stageLabel(stage: string): string {
  return STAGE_LABELS[stage] ?? humanizeStage(stage);
}

interface FunnelBreakdownProps {
  funnel: CalibrationFunnelStage[];
  offFilter: CalibrationPlanningResponse['offFilter'];
}

export function FunnelBreakdown({ funnel, offFilter }: FunnelBreakdownProps) {
  const [open, setOpen] = useState(false);

  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left hover:bg-accent/50"
            aria-expanded={open}
          >
            <span className="flex items-center gap-2 text-sm font-medium">
              <Info className="h-4 w-4 text-muted-foreground" />
              How this number is derived
            </span>
            <ChevronDown
              className={cn('h-4 w-4 text-muted-foreground transition-transform', open && 'rotate-180')}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
                    <th scope="col" className="px-3 py-2 text-left font-medium">Step</th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">Dropped</th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {funnel.map((row, idx) => (
                    <tr key={row.stage} className={cn('border-t', idx === funnel.length - 1 && 'font-semibold')}>
                      <td className="px-3 py-1.5">{stageLabel(row.stage)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                        {row.dropped > 0 ? `−${row.dropped.toLocaleString()}` : '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {row.remaining.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="mt-3 text-xs text-muted-foreground">
              Billable (uncovered) hint: {offFilter.allAccountsExpired.toLocaleString()} expired probe
              {offFilter.allAccountsExpired === 1 ? '' : 's'} across all accounts regardless of contract
              coverage, of which {offFilter.untaggedExpired.toLocaleString()} belong to org-untagged accounts.
            </p>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
